import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';

interface IGetAggregateMerchantsTransactionsRequest {
  duration: number;
  category: string;
}

interface IGetAggregateMerchantsTransactionsResponse {
  status?: boolean;
  message?: string;
  data?: {
    value?: number; // `value` and `volume` are returned for filtered categories such as BETTING, TRANSFERS, etc.
    volume?: number;
    totalValue?: number; // `totalValue` and `totalVolume` are returned for all categories and when no category is specified.
    totalVolume?: number;
    transactions: {
      transactionValue?: number;
      transactionVolume?: number;
      transactionDate?: string;
    }[];
  };
  errors?: IApiResponseError[];
}

const initialState: IApiState<IGetAggregateMerchantsTransactionsResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const getAggregateMerchantsTransactionsAsync = createAsyncThunk(
  'aggregateMerchantsTransactions',
  async (
    { duration, category }: IGetAggregateMerchantsTransactionsRequest,
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId = localStorage.getItem('businessId');

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.get(
        `/aggregators/${businessId}/transactions/merchants?duration=${duration}&category=${category}`,
        config
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;

      return rejectWithValue(error.response?.data);
    }
  }
);

export const aggregatorGetSlice = createSlice({
  name: 'aggregateMerchantsTransactions/get',
  initialState,
  reducers: {
    resetGetAggregateMerchantsTransactions: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAggregateMerchantsTransactionsAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(
        getAggregateMerchantsTransactionsAsync.fulfilled,
        (state, action) => {
          state.status = 'success';
          state.value =
            action.payload as IGetAggregateMerchantsTransactionsResponse;
        }
      )
      .addCase(
        getAggregateMerchantsTransactionsAsync.rejected,
        (state, action) => {
          state.status = 'failed';
          state.value =
            action.payload as IGetAggregateMerchantsTransactionsResponse;
          state.error = action.error.message;
        }
      );
  },
});

export const { resetGetAggregateMerchantsTransactions } =
  aggregatorGetSlice.actions;

export default aggregatorGetSlice.reducer;
